import type React from "react";
import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import type { IBuild } from "../../../model/model";
import { installViaAgent, launchViaAgent, uninstallViaAgent } from "../utils/agentInstaller";
import { useAgentConnection } from "../hooks/useAgentConnection";
import { useLaunchApp } from "../hooks/useLaunchApp";
import { getSelectedProjectGroup } from "../../../features/projectGroups/state/projectGroupsSlice";
import InstallProgressModal from "./InstallProgressModal";
import AgentUninstallButton from "./AgentUninstallButton";
import { Tooltip } from "../../../common/components/Tooltip/Tooltip";
import {
  ComputerIcon,
  WarningCircleIcon,
  DownloadArrowIcon,
  CheckIcon,
  PlayIcon,
  Spinner,
} from "./icons";

interface Props {
  build: IBuild;
}

/** Map build target_platform to the agent OS platform string */
function targetToAgentPlatform(target: string): string {
  if (target === "MacOS") return "darwin";
  if (target === "Windows10") return "win32";
  return target;
}

function agentOsLabel(platform: string): string {
  if (platform === "darwin") return "macOS";
  if (platform === "win32") return "Windows";
  return platform;
}

function targetLabel(target: string): string {
  if (target === "MacOS") return "macOS";
  if (target === "Windows10") return "Windows";
  return target;
}

const InstallOnComputerButton = ({ build }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { status: agentStatus, agentInfo } = useAgentConnection();
  const selectedPG = useAppSelector(getSelectedProjectGroup);
  const [showModal, setShowModal] = useState(false);

  const isAgentConnected = agentStatus === "connected" && agentInfo != null;

  // Check OS compatibility: build target must match agent OS
  const expectedPlatform = targetToAgentPlatform(build.target_platform);
  const isCompatible = isAgentConnected && agentInfo.os.platform === expectedPlatform;

  // Derive a stable packageName for PC builds that lack one,
  // mirroring the Android convention so extractProjectGroupId() works.
  const projectGroupId = build.project_group?.id ?? selectedPG?.id;
  const pgName = (build.project_group?.name ?? selectedPG?.name ?? "app")
    .replace(/[^a-zA-Z0-9]/g, "")
    .substring(0, 30);
  const packageName =
    build.package_name || (projectGroupId ? `com.Wixar.P${projectGroupId}_${pgName}` : null);

  // Check if already installed on agent
  const installedApp = isAgentConnected
    ? agentInfo.installedApps.find((a) => a.packageName === packageName)
    : null;
  const isInstalled = installedApp != null;
  const installedVersion = installedApp?.version;

  // Build a stable appId from packageName (agent uses this as identifier)
  const appId = packageName ?? `build-${build.id}`;

  const handleInstall = async () => {
    if (!isAgentConnected || !build.s3_url) return;
    setShowModal(true);

    const version = String(build.version);
    const displayName = build.project_group?.name ?? build.name ?? `Build ${build.id}`;
    const fileName = build.name || `wixar_build_${build.id}.zip`;

    await installViaAgent(
      {
        appId,
        displayName,
        packageName: packageName ?? appId,
        s3Url: build.s3_url,
        version,
        fileName,
      },
      dispatch,
    ).catch(() => {
      // Error already dispatched to Redux
    });
  };

  const onLaunch = useCallback(async () => {
    if (!installedApp) throw new Error("No installed app");
    await launchViaAgent(installedApp.id);
  }, [installedApp]);
  const { status: launchStatus, launch: handleLaunch } = useLaunchApp(onLaunch);

  let content: React.ReactNode = null;

  if (!isAgentConnected) {
    // Agent not connected
    content = (
      <Tooltip message={t("deviceInstaller.noAgentConnected")} classNameChildren="right-0">
        <button
          disabled
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs cursor-not-allowed bg-gray-50 text-gray-400 border border-gray-200"
        >
          <ComputerIcon />
          <span>{t("deviceInstaller.installOnComputer")}</span>
        </button>
      </Tooltip>
    );
  } else if (!isCompatible) {
    // Agent connected but wrong OS
    content = (
      <Tooltip
        message={t("deviceInstaller.incompatibleAgent", {
          os: agentOsLabel(agentInfo.os.platform),
          target: targetLabel(build.target_platform),
        })}
        classNameChildren="right-0"
      >
        <button
          disabled
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs cursor-not-allowed bg-gray-50 text-gray-400 border border-gray-200"
        >
          <ComputerIcon />
          <span>{t("deviceInstaller.installOnComputer")}</span>
        </button>
      </Tooltip>
    );
  } else if (isInstalled && installedVersion && Number(installedVersion) < build.version) {
    // Outdated
    content = (
      <div className="flex items-center gap-2">
        <Tooltip
          message={t("deviceInstaller.outdated", {
            device: installedVersion,
            latest: build.version,
          })}
          classNameChildren="right-0"
        >
          <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs bg-orange-50 text-orange-600 border border-orange-200">
            <WarningCircleIcon />V{installedVersion}
          </span>
        </Tooltip>
        <button
          onClick={handleInstall}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs cursor-pointer text-white bg-green-500 hover:opacity-90 transition-colors"
        >
          <DownloadArrowIcon />
          {t("deviceInstaller.update")} V{build.version}
        </button>
      </div>
    );
  } else if (isInstalled) {
    // Installed & up to date
    content = (
      <div className="flex items-center gap-2">
        <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs bg-gray-50 text-gray-500 border border-gray-200">
          <CheckIcon className="w-3.5 h-3.5 text-green-500" />V{build.version}
        </span>
        <Tooltip
          message={
            launchStatus === "launched"
              ? t("deviceInstaller.launchSuccess")
              : launchStatus === "error"
              ? t("deviceInstaller.retryLaunch")
              : t("deviceInstaller.launchOnComputer")
          }
          classNameChildren="right-0"
        >
          <button
            onClick={handleLaunch}
            disabled={launchStatus === "launching"}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs transition-colors ${
              launchStatus === "launched"
                ? "bg-green-50 text-green-500 border border-green-200"
                : launchStatus === "error"
                ? "bg-red-50 text-red-400 border border-red-200 hover:bg-red-100 cursor-pointer"
                : "bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 cursor-pointer"
            }`}
          >
            {launchStatus === "launching" ? (
              <Spinner className="w-3.5 h-3.5" color="green" />
            ) : launchStatus === "launched" ? (
              <CheckIcon className="w-3.5 h-3.5" />
            ) : (
              <PlayIcon className="w-3.5 h-3.5" />
            )}
            {t("deviceInstaller.launchOnComputer")}
          </button>
        </Tooltip>
        {/* Media management for desktop — disabled for now (Unity cache mismatch to investigate) */}
        {packageName && (
          <AgentUninstallButton
            onUninstall={() => uninstallViaAgent(appId)}
            onSuccess={() => {
              /* agent poll will refresh */
            }}
          />
        )}
        {/* Media push modal for desktop — disabled for now */}
      </div>
    );
  } else {
    // Not installed — default install button
    content = (
      <button
        onClick={handleInstall}
        className="relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs cursor-pointer bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 transition-colors"
      >
        <ComputerIcon />
        <span>{t("deviceInstaller.installOnComputer")}</span>
        {isAgentConnected && (
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full border-2 border-white bg-green-500" />
        )}
      </button>
    );
  }

  return (
    <>
      {content}
      {showModal && <InstallProgressModal onClose={() => setShowModal(false)} />}
    </>
  );
};

export default InstallOnComputerButton;
